import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Upload,
  Send,
  Sparkles,
  FileText,
  CheckCircle2,
  Loader2,
  User as UserIcon,
  Bot,
  ExternalLink,
  RotateCcw,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SpecImportSheet
 *
 * Props:
 *  - open, onOpenChange
 *  - manufacturerId (required)
 *  - manufacturerName (string for header)
 *  - productTypeId   (optional — when supplied, AI appends to this existing PT)
 *  - productTypeName (optional, display only)
 *  - onApplied(result) — callback after the proposal is applied
 */
export default function SpecImportSheet({
  open,
  onOpenChange,
  manufacturerId,
  manufacturerName,
  productTypeId,
  productTypeName,
  mode = "auto", // "auto" (= create or append depending on productTypeId) | "refine"
  onApplied,
}) {
  const isRefine = mode === "refine";
  const [stage, setStage] = useState(isRefine ? "starting" : "upload"); // upload | analyzing | starting | chat
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfMeta, setPdfMeta] = useState(null); // { id, filename }
  const [session, setSession] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [input, setInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [resumable, setResumable] = useState([]); // unapplied sessions available to resume
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [confirmDiscardId, setConfirmDiscardId] = useState(null);
  const [attachingPdf, setAttachingPdf] = useState(false);
  const [pdfPendingAttach, setPdfPendingAttach] = useState(false);
  const chatBottomRef = useRef(null);
  const startedRef = useRef(false); // guard against React StrictMode double-effect

  useEffect(() => {
    if (!open) {
      // reset everything when closed
      setStage(isRefine ? "starting" : "upload");
      setPdfFile(null);
      setPdfMeta(null);
      setSession(null);
      setInput("");
      setThinking(false);
      setApplying(false);
      setResumable([]);
      setAttachingPdf(false);
      setPdfPendingAttach(false);
      startedRef.current = false;
      return;
    }
    if (isRefine) return; // refine has its own start effect below
    // Look for unfinished sessions to offer "Resume"
    (async () => {
      try {
        const { data } = await api.get(
          `/manufacturers/${manufacturerId}/spec-import/sessions?only_unapplied=true`
        );
        // Filter by PT scope: if drawer is opened for a specific PT, only show resumes for that PT;
        // if opened at manufacturer level, only show resumes that target a new PT (product_type_id=null).
        // Refine sessions are excluded from the "Add via spec sheet" resume list.
        const filtered = (data || [])
          .filter((s) => s.mode !== "refine")
          .filter((s) =>
            productTypeId ? s.product_type_id === productTypeId : !s.product_type_id
          );
        setResumable(filtered);
      } catch {
        setResumable([]);
      }
    })();
  }, [open, manufacturerId, productTypeId, isRefine]);

  // Auto-start a refine session when the drawer opens in refine mode
  useEffect(() => {
    if (!open || !isRefine || startedRef.current || !productTypeId) return;
    startedRef.current = true;
    setStage("starting");
    setThinking(true);
    (async () => {
      try {
        const { data } = await api.post(
          `/manufacturers/${manufacturerId}/spec-import/refine-sessions`,
          { product_type_id: productTypeId }
        );
        setSession(data);
        setStage("chat");
      } catch (e) {
        toast.error(formatApiError(e, "Could not start a refine session"));
        startedRef.current = false;
        onOpenChange(false);
      } finally {
        setThinking(false);
      }
    })();
  }, [open, isRefine, productTypeId, manufacturerId, onOpenChange]);

  const resumeSession = useCallback(async (sessionId) => {
    setStage("analyzing");
    try {
      const { data } = await api.get(`/spec-import/sessions/${sessionId}`);
      setSession(data);
      if (data.pdf_id) {
        setPdfMeta({ id: data.pdf_id, filename: data.pdf_filename || "spec.pdf" });
      }
      setStage("chat");
      startedRef.current = true;
    } catch (e) {
      toast.error(formatApiError(e, "Could not resume that session"));
      setStage("upload");
    }
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages?.length, thinking]);

  const startAnalysis = useCallback(async (file) => {
    if (!file || startedRef.current) return;
    startedRef.current = true;
    setStage("analyzing");
    setThinking(true);
    try {
      // 1) upload PDF
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/pdfs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfMeta(up.data);
      // 2) create session — AI gives first proposal
      const { data } = await api.post(
        `/manufacturers/${manufacturerId}/spec-import/sessions`,
        {
          pdf_id: up.data.id,
          product_type_id: productTypeId || null,
        }
      );
      setSession(data);
      setStage("chat");
    } catch (e) {
      toast.error(formatApiError(e, "Could not start the AI session"));
      setStage("upload");
      setPdfFile(null);
      startedRef.current = false;
    } finally {
      setThinking(false);
    }
  }, [manufacturerId, productTypeId]);

  // Auto-start analysis as soon as a PDF is selected
  useEffect(() => {
    if (pdfFile && stage === "upload") {
      startAnalysis(pdfFile);
    }
  }, [pdfFile, stage, startAnalysis]);

  // Detect when the user's chat message is really an "apply this" signal.
  const APPROVAL_REGEX = /^(?:\s*(?:please\s+)?)(?:approve(?:d)?|apply(?:\s+it)?|do\s+it|save(?:\s+it)?|commit|go\s+ahead|looks?\s+good|that\s+looks?\s+good|perfect|yes(?:\s+(?:please|do\s+it))?|ship\s+it|👍|✅)\s*[.!]?\s*$/i;

  const looksLikeApprove = (text) =>
    APPROVAL_REGEX.test(text.trim()) &&
    !!(session?.latest_proposal?.product_type?.columns?.length);

  const send = async () => {
    if (!input.trim() || !session) return;
    const msg = input.trim();

    // Short-circuit: if the user is clearly approving, just apply the proposal
    if (looksLikeApprove(msg)) {
      setInput("");
      await apply();
      return;
    }

    setInput("");
    // optimistic add of user message
    setSession((s) => ({
      ...s,
      messages: [
        ...(s?.messages || []),
        { role: "user", content: msg, ts: new Date().toISOString() },
      ],
    }));
    setThinking(true);
    try {
      const { data } = await api.post(`/spec-import/sessions/${session.id}/message`, {
        message: msg,
      });
      setSession((prev) => ({ ...prev, messages: data.messages, latest_proposal: data.latest_proposal }));
      // Backend clears pdf_needs_attach after consuming it on this turn — reflect that locally.
      setPdfPendingAttach(false);
    } catch (e) {
      toast.error(formatApiError(e, "AI reply failed — your message was kept, try sending again."));
      // Roll back the optimistic user message and restore the input so the user can retry
      setSession((prev) =>
        prev
          ? {
              ...prev,
              messages: (prev.messages || []).filter(
                (_, idx, arr) => !(idx === arr.length - 1 && arr[idx]?.role === "user")
              ),
            }
          : prev
      );
      setInput(msg);
    } finally {
      setThinking(false);
    }
  };

  const attachPdf = async (file) => {
    if (!file || !session) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please drop a PDF file.");
      return;
    }
    setAttachingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/pdfs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/spec-import/sessions/${session.id}/attach-pdf`, {
        pdf_id: up.data.id,
      });
      setPdfMeta(up.data);
      setPdfPendingAttach(true);
      toast.success(`Attached "${up.data.filename}" — it'll be sent with your next message.`);
    } catch (e) {
      toast.error(formatApiError(e, "Could not attach the PDF"));
    } finally {
      setAttachingPdf(false);
    }
  };

  const apply = async () => {
    if (!session) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/spec-import/sessions/${session.id}/apply`);
      if (data.mode === "refine") {
        const updated = data.products_updated || 0;
        const created = data.products_created || 0;
        const parts = [];
        if (updated) parts.push(`${updated} row${updated === 1 ? "" : "s"} updated`);
        if (created) parts.push(`${created} row${created === 1 ? "" : "s"} added`);
        if (!parts.length) parts.push("schema saved");
        toast.success(`"${data.product_type.name}" refined — ${parts.join(", ")}`);
      } else {
        toast.success(
          `Created ${data.products_created} product${data.products_created === 1 ? "" : "s"} in "${data.product_type.name}"`
        );
      }
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Could not apply"));
    } finally {
      setApplying(false);
    }
  };

  const resetToUpload = () => {
    setSession(null);
    setPdfFile(null);
    setPdfMeta(null);
    setInput("");
    setThinking(false);
    setStage("upload");
    startedRef.current = false;
  };

  const startOver = async ({ deleteSession } = { deleteSession: true }) => {
    const sid = session?.id;
    resetToUpload();
    if (deleteSession && sid) {
      try {
        await api.delete(`/spec-import/sessions/${sid}`);
      } catch {
        /* ignore — UI already reset */
      }
    }
    // refresh resume list
    try {
      const { data } = await api.get(
        `/manufacturers/${manufacturerId}/spec-import/sessions?only_unapplied=true`
      );
      const filtered = (data || []).filter((s) =>
        productTypeId ? s.product_type_id === productTypeId : !s.product_type_id
      );
      setResumable(filtered);
    } catch {
      /* ignore */
    }
  };

  const discardResumable = async (sid) => {
    try {
      await api.delete(`/spec-import/sessions/${sid}`);
      setResumable((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) {
      toast.error(formatApiError(e, "Could not discard"));
    }
  };

  const proposal = session?.latest_proposal;
  const productCount = proposal?.products?.length || 0;
  const columnCount = proposal?.product_type?.columns?.length || 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 bg-white border-l flex flex-col"
      >
        <SheetHeader className="p-5 border-b border-border shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-600 font-mono-data">
                <Sparkles className="w-3.5 h-3.5" />
                {isRefine ? "AI refine" : "AI spec sheet import"}
              </div>
              <SheetTitle className="font-display tracking-tight mt-1 truncate">
                {manufacturerName}
                {productTypeName ? (
                  <span className="text-muted-foreground"> · {productTypeName}</span>
                ) : null}
              </SheetTitle>
              <SheetDescription className="mt-1">
                {isRefine
                  ? "Chat with the AI to fix mistakes, tweak columns/aliases, or add missing rows to this product type. No new PDF needed — the AI already knows what's in the database."
                  : productTypeId
                  ? "Upload a spec sheet and the AI will propose new rows to append to this product type."
                  : "Upload a spec sheet and the AI will propose a new product type with columns and product rows."}
              </SheetDescription>
            </div>
            {stage === "chat" && !isRefine && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmStartOver(true)}
                className="rounded-sm shrink-0"
                data-testid="spec-start-over-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Start over
              </Button>
            )}
          </div>
        </SheetHeader>

        {stage === "upload" ? (
          <UploadStage
            pdfFile={pdfFile}
            setPdfFile={setPdfFile}
            resumable={resumable}
            onResume={resumeSession}
            onDiscardRequest={(id) => setConfirmDiscardId(id)}
          />
        ) : stage === "analyzing" ? (
          <AnalyzingStage pdfFile={pdfFile} />
        ) : stage === "starting" ? (
          <StartingRefineStage productTypeName={productTypeName} />
        ) : (
          <ChatStage
            session={session}
            thinking={thinking}
            input={input}
            setInput={setInput}
            onSend={send}
            chatBottomRef={chatBottomRef}
            pdfMeta={pdfMeta}
            isRefine={isRefine}
            onAttachPdf={attachPdf}
            attachingPdf={attachingPdf}
            pdfPendingAttach={pdfPendingAttach}
          />
        )}

        {stage === "chat" && (
          <SheetFooter className="p-5 border-t border-border bg-slate-50 flex sm:justify-between gap-3 items-stretch sm:items-center shrink-0">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {proposal ? (
                isRefine ? (
                  <Badge variant="outline" className="rounded-sm border-amber-300 bg-amber-50 text-amber-700 font-mono-data">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Refine: {columnCount} cols · {productCount} {productCount === 1 ? "row change" : "row changes"}
                  </Badge>
                ) : productCount > 0 ? (
                  <Badge variant="outline" className="rounded-sm border-amber-300 bg-amber-50 text-amber-700 font-mono-data">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Proposal: {columnCount} cols · {productCount} {productCount === 1 ? "row" : "rows"} · ready to apply
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-sm border-slate-300 bg-slate-100 text-slate-700 font-mono-data">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {columnCount} columns · awaiting rows — answer the AI's question
                  </Badge>
                )
              ) : (
                <span>Waiting for first proposal…</span>
              )}
            </div>
            <Button
              onClick={apply}
              disabled={!proposal || applying || !columnCount}
              className={`bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm font-semibold ${
                proposal && (productCount || isRefine) && !applying ? "byrd-pulse-ready" : ""
              }`}
              data-testid="apply-spec-proposal-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {isRefine
                ? productCount > 0
                  ? "Save refinements"
                  : "Save column changes"
                : productCount > 0
                ? "Apply to database"
                : "Apply columns only"}
            </Button>
          </SheetFooter>
        )}

        {/* Confirm: Start over (chat stage) */}
        <AlertDialog open={confirmStartOver} onOpenChange={setConfirmStartOver}>
          <AlertDialogContent className="rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Start over with a new spec sheet?</AlertDialogTitle>
              <AlertDialogDescription>
                This will discard the current AI conversation and any unsaved proposal. You'll be returned to the upload screen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmStartOver(false);
                  startOver({ deleteSession: true });
                }}
                className="bg-destructive hover:bg-red-600 text-white rounded-sm"
                data-testid="confirm-start-over"
              >
                Start over
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm: Discard a resumable session */}
        <AlertDialog
          open={!!confirmDiscardId}
          onOpenChange={(o) => !o && setConfirmDiscardId(null)}
        >
          <AlertDialogContent className="rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Discard this unfinished import?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove the in-progress AI session. The PDF you uploaded is kept; only the chat is removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const id = confirmDiscardId;
                  setConfirmDiscardId(null);
                  if (id) discardResumable(id);
                }}
                className="bg-destructive hover:bg-red-600 text-white rounded-sm"
                data-testid="confirm-discard-session"
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

function UploadStage({ pdfFile, setPdfFile, resumable, onResume, onDiscardRequest }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please drop a PDF file.");
      return;
    }
    setPdfFile(file);
  };

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      {resumable && resumable.length > 0 && (
        <div className="mb-5 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="resume-banner">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-sm bg-amber-500 flex items-center justify-center shrink-0 mt-0.5">
              <FileText className="w-3.5 h-3.5 text-slate-900" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                You have {resumable.length} unfinished import{resumable.length === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Pick up where you left off — your chat history and current proposal are saved.
              </div>
              <div className="mt-2 space-y-1.5">
                {resumable.slice(0, 3).map((s) => (
                  <div
                    key={s.id}
                    className="bg-white border border-border rounded-sm flex items-center justify-between gap-2 hover:border-slate-900 transition-colors"
                  >
                    <button
                      onClick={() => onResume(s.id)}
                      className="flex-1 text-left px-3 py-2 min-w-0"
                      data-testid={`resume-session-${s.id}`}
                    >
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {s.pdf_filename || "Untitled PDF"}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono-data uppercase tracking-wider">
                        {s.messages_count} messages · {s.has_proposal ? "proposal ready" : "in progress"}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 pr-2">
                      <button
                        onClick={() => onResume(s.id)}
                        className="text-amber-600 hover:text-amber-700 text-xs px-2 py-1 font-medium"
                      >
                        Resume →
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDiscardRequest(s.id);
                        }}
                        className="p-1 text-muted-foreground hover:text-destructive"
                        aria-label="Discard"
                        data-testid={`discard-session-${s.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <label
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
        }}
        onDrop={onDrop}
        className={`block border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${
          dragging
            ? "border-amber-500 bg-amber-50"
            : pdfFile
            ? "border-amber-400 bg-amber-50"
            : "border-border bg-slate-50 hover:border-slate-400"
        }`}
        data-testid="spec-pdf-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
          data-testid="spec-pdf-input"
        />
        {pdfFile ? (
          <div>
            <FileText className="w-10 h-10 mx-auto text-amber-600" />
            <div className="mt-3 font-display font-semibold text-slate-900">{pdfFile.name}</div>
            <div className="text-xs text-muted-foreground font-mono-data uppercase tracking-wider mt-1">
              {(pdfFile.size / 1024).toFixed(0)} KB · starting analysis…
            </div>
          </div>
        ) : (
          <div>
            <Upload className="w-10 h-10 mx-auto text-slate-500" />
            <div className="mt-3 font-display font-semibold text-slate-900">
              {dragging ? "Drop the PDF to start" : "Drop a spec sheet PDF here"}
            </div>
            <div className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
              Or <span className="text-amber-600 underline underline-offset-2">click to choose a file</span>. The AI starts reading the moment you upload — no extra clicks needed.
            </div>
            <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
              PDF up to 20 MB
            </div>
          </div>
        )}
      </label>

      <div className="mt-8 text-xs text-muted-foreground space-y-2 max-w-md">
        <div className="text-[10px] uppercase tracking-widest font-mono-data text-slate-500">
          What works best
        </div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Manufacturer-issued PDF spec sheets with model number, size/CCT/wattage options, accessories.</li>
          <li>One product family per PDF. For combinatorial options the AI will ask before generating dozens of rows.</li>
          <li>You can refine after analysis: "merge wattages", "add IP rating", "only 4000K rows".</li>
        </ul>
      </div>
    </div>
  );
}

function AnalyzingStage({ pdfFile }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="spec-analyzing">
      <div className="relative">
        <div className="w-20 h-20 rounded-sm bg-amber-500 flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-slate-900" />
        </div>
        <div className="absolute -bottom-1 -right-1 bg-slate-900 text-white rounded-sm p-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
      </div>
      <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900">
        Reading your spec sheet…
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        Gemini 2.5 Pro is analyzing <span className="font-medium text-slate-900">{pdfFile?.name || "the PDF"}</span> page by page, identifying the product family, options, and how to structure the data. This usually takes 10–30 seconds.
      </p>
      <div className="mt-6 byrd-progress w-64" />
      <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
        You'll see the proposal as soon as it's ready
      </div>
    </div>
  );
}

function StartingRefineStage({ productTypeName }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="spec-refine-starting">
      <div className="relative">
        <div className="w-20 h-20 rounded-sm bg-slate-900 flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-amber-400" />
        </div>
        <div className="absolute -bottom-1 -right-1 bg-amber-500 text-slate-900 rounded-sm p-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
      </div>
      <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900">
        Loading <span className="text-amber-600">{productTypeName || "this product type"}</span>…
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        Pulling the current columns, aliases, part-number template, and product rows so the AI has full context before you start refining.
      </p>
      <div className="mt-6 byrd-progress w-64" />
    </div>
  );
}

function ChatStage({
  session,
  thinking,
  input,
  setInput,
  onSend,
  chatBottomRef,
  pdfMeta,
  isRefine,
  onAttachPdf,
  attachingPdf,
  pdfPendingAttach,
}) {
  const messages = session?.messages || [];
  const proposal = session?.latest_proposal;
  const attachInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!isRefine || !onAttachPdf) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onAttachPdf(file);
  };

  return (
    <div
      className={`flex-1 grid grid-rows-[1fr_auto] min-h-0 relative ${
        dragOver ? "ring-4 ring-amber-300 ring-inset" : ""
      }`}
      onDragOver={(e) => {
        if (!isRefine || !onAttachPdf) return;
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
      }}
      onDrop={handleDrop}
    >
      {dragOver && isRefine && (
        <div className="absolute inset-0 z-10 bg-amber-50/95 flex flex-col items-center justify-center pointer-events-none">
          <Upload className="w-10 h-10 text-amber-600" />
          <div className="mt-2 font-display font-semibold text-slate-900">
            Drop the PDF to attach it
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            The AI will read it on your next message
          </div>
        </div>
      )}
      <div className="overflow-y-auto p-5 space-y-4 bg-slate-50/50">
        {pdfMeta ? (
          <div className="inline-flex items-center gap-2 text-xs">
            <a
              href={`${BACKEND_URL}/api/pdfs/${pdfMeta.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 bg-white border border-border rounded-sm px-3 py-1.5"
            >
              <FileText className="w-3.5 h-3.5 text-amber-600" />
              <span className="font-medium">{pdfMeta.filename}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
            {pdfPendingAttach && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-data bg-amber-100 text-amber-800 border border-amber-300 rounded-sm px-2 py-1"
                data-testid="pdf-pending-attach"
              >
                <Sparkles className="w-3 h-3" /> queued · sent with next message
              </span>
            )}
          </div>
        ) : isRefine ? (
          <button
            type="button"
            onClick={() => attachInputRef.current?.click()}
            disabled={attachingPdf}
            className="inline-flex items-center gap-2 text-xs text-slate-700 hover:text-slate-900 bg-white border border-dashed border-amber-300 rounded-sm px-3 py-1.5 disabled:opacity-50"
            data-testid="refine-attach-pdf-btn"
          >
            {attachingPdf ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
            ) : (
              <Upload className="w-3.5 h-3.5 text-amber-600" />
            )}
            <span className="font-medium">
              {attachingPdf ? "Uploading…" : "Attach spec sheet (optional)"}
            </span>
            <span className="text-muted-foreground">— or drag-drop anywhere</span>
          </button>
        ) : null}
        <input
          ref={attachInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onAttachPdf) onAttachPdf(file);
            e.target.value = ""; // reset so the same file can be re-picked
          }}
          data-testid="refine-attach-pdf-input"
        />
        {messages.map((m, i) => (
          <ChatBubble key={i} message={m} />
        ))}
        {thinking && (
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-sm bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div className="bg-white border border-border rounded-sm px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI is reading…
            </div>
          </div>
        )}

        {proposal && <ProposalPreview proposal={proposal} />}

        <div ref={chatBottomRef} />
      </div>

      <div className="border-t border-border p-3 bg-white shrink-0">
        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={
              isRefine
                ? 'Tell the AI what to fix — e.g. "add wet location to the aliases" or "the 4000K row should be 4500lm"'
                : 'Refine the proposal — e.g. "merge wattages into one column" or "add IP rating"'
            }
            className="rounded-sm resize-none"
            data-testid="spec-chat-input"
          />
          <Button
            onClick={onSend}
            disabled={!input.trim() || thinking}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm h-[60px]"
            data-testid="spec-chat-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <div className="mt-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Press Enter to send · Shift+Enter for newline · Type{" "}
          <span className="text-amber-600">"apply"</span> to commit
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === "user";
  // For assistant, display only the prose (strip the JSON block)
  const text = isUser
    ? message.content
    : (message.prose || stripJsonBlock(message.content || "") || message.content);
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${
          isUser ? "bg-amber-500 text-slate-900" : "bg-slate-900 text-white"
        }`}
      >
        {isUser ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div
        className={`max-w-[85%] text-sm whitespace-pre-wrap leading-relaxed rounded-sm px-3 py-2 border ${
          isUser ? "bg-amber-50 border-amber-200 text-slate-900" : "bg-white border-border text-slate-800"
        }`}
      >
        {text || "…"}
      </div>
    </div>
  );
}

function stripJsonBlock(text) {
  return text.replace(/```json[\s\S]*?```/g, "").trim();
}

function ProposalPreview({ proposal }) {
  const pt = proposal.product_type || {};
  const columns = pt.columns || [];
  const products = proposal.products || [];
  const template = pt.part_number_template;
  const valueCodes = pt.value_codes || {};
  const aliases = pt.aliases || [];
  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border bg-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Current proposal
          </div>
          <div className="font-display font-semibold text-slate-900 text-sm">
            {pt.name || "(unnamed product type)"}
            <span className="text-muted-foreground font-normal text-xs ml-2">
              {columns.length} cols · {products.length} {products.length === 1 ? "row" : "rows"}
            </span>
          </div>
        </div>
      </div>

      {aliases.length > 0 && (
        <div className="px-4 py-2.5 border-b border-border bg-amber-50/40">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data mb-1.5">
            Aliases · distributor search terms
          </div>
          <div className="flex flex-wrap gap-1.5">
            {aliases.map((a) => (
              <span
                key={a}
                className="inline-block px-2 py-0.5 rounded-sm bg-white border border-amber-200 text-amber-800 text-xs font-medium"
              >
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {template && (
        <div className="px-4 py-2.5 border-b border-border bg-amber-50/40">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data mb-1">
            Part number template
          </div>
          <div className="font-mono-data text-sm text-slate-900 break-all">{template}</div>
          {Object.keys(valueCodes).length > 0 && (
            <details className="mt-1">
              <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-slate-900">
                Show {Object.keys(valueCodes).length} value→code mapping{Object.keys(valueCodes).length === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 space-y-1 text-[11px]">
                {Object.entries(valueCodes).map(([k, m]) => (
                  <div key={k} className="font-mono-data">
                    <span className="text-amber-700">{k}:</span>{" "}
                    {Object.entries(m).map(([v, c]) => (
                      <span key={v} className="inline-block mr-2">
                        <span className="text-slate-700">{v}</span>
                        <span className="text-muted-foreground"> → </span>
                        <span className="text-slate-900 font-medium">{c}</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {columns.length > 0 && (
        <div className="px-4 py-3 border-b border-border flex flex-wrap gap-1.5">
          {columns.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1.5 text-[11px] font-mono-data uppercase tracking-wider bg-slate-50 border border-border px-2 py-0.5 rounded-sm text-slate-700"
              title={c.type + (c.options ? `: ${c.options.join(", ")}` : "")}
            >
              {c.label}
              <span className="text-amber-600">{c.type}</span>
            </span>
          ))}
        </div>
      )}

      {products.length > 0 && (
        <div className="max-h-72 overflow-auto">
          <table className="byrd-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Model #</th>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => (
                <tr key={idx}>
                  <td>{p.name}</td>
                  <td className="font-mono-data">{p.model_number}</td>
                  {columns.map((c) => (
                    <td key={c.key}>{renderProposalCell(c, p.attributes?.[c.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {proposal.notes && (
        <div className="px-4 py-2.5 border-t border-border text-xs text-muted-foreground italic">
          {proposal.notes}
        </div>
      )}
    </div>
  );
}

function renderProposalCell(column, value) {
  if (column.type === "option") {
    const v = ["Standard", "Optional", "Not Available"].includes(value) ? value : "Optional";
    const palette = {
      Standard: "bg-emerald-50 border-emerald-300 text-emerald-700",
      Optional: "bg-amber-50 border-amber-300 text-amber-700",
      "Not Available": "bg-slate-100 border-border text-muted-foreground",
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium border ${palette[v]}`}>{v}</span>
    );
  }
  if (column.type === "multiselect") {
    const arr = Array.isArray(value) ? value : value ? [String(value)] : [];
    if (arr.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {arr.map((v) => (
          <span
            key={v}
            className="inline-block px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium"
          >
            {v}
          </span>
        ))}
      </div>
    );
  }
  return formatVal(value, column.type);
}

function formatVal(v, columnType) {
  if (columnType === "option") {
    return ["Standard", "Optional", "Not Available"].includes(v) ? v : (v || "Optional");
  }
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v == null || v === "") return "";
  return String(v);
}
